import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { httpResource } from '@angular/common/http';
import { BillingDocumentService } from '../../Modules/Billing/Services/billing-document.service';
import { OrganizationService } from '../../Modules/Organizations/Services/organization.service';
import { ResourceStateService } from '../../Modules/Shared/Services/resource-state.service';
import { ErrorHandlerService } from '../../Modules/Shared/Services/error-handler.service';
import { ExternalLinkService } from '../../Modules/Shared/Services/external-link.service';
import { MsgService } from '../../Modules/Shared/Services/msg.service';
import { OperatorFacade } from '../operator.facade';
import { DEFAULT_PAGE_SIZE, FIRST_PAGE } from '../../Consts/backoffice.const';
import {
  PAYMENT_RETRIED_MESSAGE,
  REFUND_ALREADY_RECORDED_MESSAGE,
  REFUND_CREATED_MESSAGE,
  ROLE_MONEY_OUT,
} from '../../Consts/billing-documents.const';
import { MetricState } from '../../Modules/Shared/Types/metric-state.type';
import { CreditNote } from '../../Models/backoffice/credit-note.model';
import { InvoiceSummary } from '../../Models/backoffice/invoice-summary.model';
import { PaymentSummary } from '../../Models/backoffice/payment-summary.model';
import { Refund } from '../../Models/backoffice/refund.model';

import { LaravelDefaultResponse } from '../../Interfaces/api/shared/response.interface';
import { OrganizationShowResponse } from '../../Interfaces/api/backoffice/organization.interface';
import {
  CreditNoteListResponse,
  InvoiceListResponse,
  PaymentAttentionResponse,
  PaymentAttentionStateResponse,
  PaymentListResponse,
  RefundListResponse,
  RefundPayload,
} from '../../Interfaces/api/backoffice/billing-document.interface';
import { InvoiceFilters } from '../../Modules/Billing/Interfaces/billing-dialog.interface';

@Injectable()
export class BillingDocumentsFacade {
  private readonly _documentService = inject(BillingDocumentService);
  private readonly _organizationService = inject(OrganizationService);
  private readonly _resourceState = inject(ResourceStateService);
  private readonly _errorHandler = inject(ErrorHandlerService);
  private readonly _externalLink = inject(ExternalLinkService);
  private readonly _msgService = inject(MsgService);
  private readonly _operatorFacade = inject(OperatorFacade);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _organizationId = signal<string>('');
  private readonly _invoicePage = signal<number>(FIRST_PAGE);
  private readonly _paymentStatus = signal<string>('');
  private readonly _paymentPage = signal<number>(FIRST_PAGE);
  private readonly _refundPage = signal<number>(FIRST_PAGE);
  private readonly _creditNotePage = signal<number>(FIRST_PAGE);
  private readonly _pageSize = signal<number>(DEFAULT_PAGE_SIZE);
  private readonly _isMutating = signal<boolean>(false);

  private readonly _invoiceFilters = signal<InvoiceFilters>({
    stripe_status: '',
    attention: '',
    from: '',
    to: '',
  });

  readonly canMoveMoney = computed<boolean>(() => this._operatorFacade.atLeast(ROLE_MONEY_OUT));

  private readonly _organization = httpResource<LaravelDefaultResponse<OrganizationShowResponse>>(
    () =>
      this._organizationId()
        ? this._organizationService.showRequest(this._organizationId())
        : undefined,
  );

  private readonly _invoices = httpResource<LaravelDefaultResponse<InvoiceListResponse>>(() =>
    this._organizationId()
      ? this._documentService.invoicesRequest(
          this._organizationId(),
          this._invoiceFilters(),
          this._invoicePage(),
          this._pageSize(),
        )
      : undefined,
  );

  private readonly _payments = httpResource<LaravelDefaultResponse<PaymentListResponse>>(() =>
    this._organizationId()
      ? this._documentService.paymentsRequest(
          this._organizationId(),
          this._paymentStatus(),
          this._paymentPage(),
          this._pageSize(),
        )
      : undefined,
  );

  private readonly _attention = httpResource<LaravelDefaultResponse<PaymentAttentionResponse>>(() =>
    this._organizationId()
      ? this._documentService.attentionRequest(this._organizationId())
      : undefined,
  );

  private readonly _refunds = httpResource<LaravelDefaultResponse<RefundListResponse>>(() =>
    this._organizationId() && this.canMoveMoney()
      ? this._documentService.refundsRequest(
          this._organizationId(),
          this._refundPage(),
          this._pageSize(),
        )
      : undefined,
  );

  private readonly _creditNotes = httpResource<LaravelDefaultResponse<CreditNoteListResponse>>(() =>
    this._organizationId() && this.canMoveMoney()
      ? this._documentService.creditNotesRequest(
          this._organizationId(),
          this._creditNotePage(),
          this._pageSize(),
        )
      : undefined,
  );

  readonly organizationId = computed<string>(() => this._organizationId());

  readonly organizationName = computed<string>(
    () => this._organizationService.toDetail(this._resourceState.valueOf(this._organization))?.displayName ?? '',
  );

  readonly invoiceFilters = computed<InvoiceFilters>(() => this._invoiceFilters());

  readonly invoicePage = computed<number>(() => this._invoicePage());

  readonly paymentStatus = computed<string>(() => this._paymentStatus());

  readonly paymentPage = computed<number>(() => this._paymentPage());

  readonly refundPage = computed<number>(() => this._refundPage());

  readonly creditNotePage = computed<number>(() => this._creditNotePage());

  readonly pageSize = computed<number>(() => this._pageSize());

  readonly isMutating = computed<boolean>(() => this._isMutating());

  readonly invoices = computed<InvoiceSummary[]>(
    () => this._documentService.toInvoices(this._resourceState.valueOf(this._invoices)).items,
  );

  readonly invoiceTotal = computed<number>(
    () => this._documentService.toInvoices(this._resourceState.valueOf(this._invoices)).total,
  );

  readonly invoicesState = computed<MetricState>(() =>
    this._resourceState.metricState(this._invoices.status()),
  );

  readonly payments = computed<PaymentSummary[]>(
    () => this._documentService.toPayments(this._resourceState.valueOf(this._payments)).items,
  );

  readonly paymentTotal = computed<number>(
    () => this._documentService.toPayments(this._resourceState.valueOf(this._payments)).total,
  );

  readonly paymentsState = computed<MetricState>(() =>
    this._resourceState.metricState(this._payments.status()),
  );

  readonly attention = computed<PaymentAttentionStateResponse | null>(() =>
    this._documentService.toAttention(this._resourceState.valueOf(this._attention)),
  );

  readonly attentionState = computed<MetricState>(() =>
    this._resourceState.metricState(this._attention.status()),
  );

  readonly refunds = computed<Refund[]>(
    () => this._documentService.toRefunds(this._resourceState.valueOf(this._refunds)).items,
  );

  readonly refundTotal = computed<number>(
    () => this._documentService.toRefunds(this._resourceState.valueOf(this._refunds)).total,
  );

  readonly refundsState = computed<MetricState>(() =>
    this._resourceState.metricState(this._refunds.status(), this.canMoveMoney()),
  );

  readonly creditNotes = computed<CreditNote[]>(
    () => this._documentService.toCreditNotes(this._resourceState.valueOf(this._creditNotes)).items,
  );

  readonly creditNoteTotal = computed<number>(
    () => this._documentService.toCreditNotes(this._resourceState.valueOf(this._creditNotes)).total,
  );

  readonly creditNotesState = computed<MetricState>(() =>
    this._resourceState.metricState(this._creditNotes.status(), this.canMoveMoney()),
  );

  readonly isOwned = computed<boolean>(() => this.attention()?.is_owned ?? true);

  readonly canWrite = computed<boolean>(
    () => this.canMoveMoney() && this.isOwned() && !this._isMutating(),
  );

  readonly hasInvoiceFilters = computed<boolean>(() =>
    Object.values(this._invoiceFilters()).some((value) => value !== ''),
  );

  load(organizationId: string): void {
    this._organizationId.set(organizationId);
  }

  applyInvoiceFilters(filters: InvoiceFilters): void {
    this._invoiceFilters.set(filters);
    this._invoicePage.set(FIRST_PAGE);
  }

  clearInvoiceFilters(): void {
    this.applyInvoiceFilters({ stripe_status: '', attention: '', from: '', to: '' });
  }

  filterPayments(status: string): void {
    this._paymentStatus.set(status);
    this._paymentPage.set(FIRST_PAGE);
  }

  goToInvoicePage(page: number): void {
    this._invoicePage.set(Math.max(FIRST_PAGE, page));
  }

  goToPaymentPage(page: number): void {
    this._paymentPage.set(Math.max(FIRST_PAGE, page));
  }

  goToRefundPage(page: number): void {
    this._refundPage.set(Math.max(FIRST_PAGE, page));
  }

  goToCreditNotePage(page: number): void {
    this._creditNotePage.set(Math.max(FIRST_PAGE, page));
  }

  changePageSize(pageSize: number): void {
    this._pageSize.set(pageSize);
    this._invoicePage.set(FIRST_PAGE);
    this._paymentPage.set(FIRST_PAGE);
    this._refundPage.set(FIRST_PAGE);
    this._creditNotePage.set(FIRST_PAGE);
  }

  reload(): void {
    this._invoices.reload();
    this._payments.reload();
    this._attention.reload();
    this._refunds.reload();
    this._creditNotes.reload();
  }

  openDocument(invoiceId: string): void {
    this._documentService
      .documentUrl(this._organizationId(), invoiceId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (url) => this._externalLink.openInNewTab(url),
        error: (error) => this._failMutation(error),
      });
  }

  retryPayment(paymentId: string, reason: string): void {
    if (!this.canWrite()) {
      return;
    }

    this._isMutating.set(true);

    this._documentService
      .retryPayment(this._organizationId(), paymentId, reason)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterWrite(PAYMENT_RETRIED_MESSAGE),
        error: (error) => this._failMutation(error),
      });
  }

  refundPayment(paymentId: string, payload: RefundPayload): void {
    if (!this.canWrite()) {
      return;
    }

    this._isMutating.set(true);

    this._documentService
      .refundPayment(this._organizationId(), paymentId, payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (result) =>
          this._afterWrite(
            result.already_recorded ? REFUND_ALREADY_RECORDED_MESSAGE : REFUND_CREATED_MESSAGE,
          ),
        error: (error) => this._failMutation(error),
      });
  }

  private _afterWrite(message: string): void {
    this._isMutating.set(false);
    this.reload();
    this._msgService.showSnackBar(message, { type: 'success' });
  }

  private _failMutation(error: unknown): void {
    this._isMutating.set(false);
    this._msgService.showSnackBar(this._errorHandler.messageFor(error), { type: 'error' });
  }
}
